import { getThemePreset, type PlayfieldPattern } from './catalog';
import { machineArtForTable, THEME_ART_ATLAS_URL, THEME_ART_FRAME_COUNT } from './themeMachineArt';
import type { TableDefinition } from '../engine/tables/definition';

const KNOWN_PATTERNS: ReadonlySet<string> = new Set([
  'rings',
  'circuit',
  'stars',
  'rays',
  'checker',
  'waves',
  'hex',
  'hazard',
  'minimal',
]);

export interface ThemeStagePresentation {
  backgroundColor: string;
  backgroundImage: string;
  backgroundSize: string;
  backgroundPosition: string;
  themeName: string;
}

function translucent(color: string, percent: number): string {
  return `color-mix(in srgb, ${color} ${percent}%, transparent)`;
}

function motifFor(
  pattern: PlayfieldPattern,
  accent: string,
  secondary: string,
): { image: string; size: string; position: string } {
  switch (pattern) {
    case 'rings':
      return {
        image: `repeating-radial-gradient(circle at 24% 50%, transparent 0 2.7rem, ${translucent(accent, 24)} 2.78rem 2.92rem, transparent 3rem 5.2rem)`,
        size: 'auto',
        position: 'center',
      };
    case 'circuit':
      return {
        image: `repeating-linear-gradient(90deg, transparent 0 4.8rem, ${translucent(accent, 18)} 4.82rem 4.9rem, transparent 4.92rem 8.4rem)`,
        size: 'auto',
        position: 'center',
      };
    case 'stars':
      return {
        image: `radial-gradient(circle, ${translucent(secondary, 72)} 0 1.15px, transparent 1.6px)`,
        size: '2.5rem 2.5rem',
        position: '0.7rem 0.4rem',
      };
    case 'rays':
      return {
        image: `repeating-conic-gradient(from -18deg at 26% 52%, transparent 0 12deg, ${translucent(accent, 17)} 12deg 14deg, transparent 14deg 28deg, ${translucent(secondary, 12)} 28deg 29deg, transparent 29deg 42deg)`,
        size: 'auto',
        position: 'center',
      };
    case 'checker':
      return {
        image: `conic-gradient(from 45deg, ${translucent(accent, 15)} 0 25%, transparent 0 50%, ${translucent(secondary, 13)} 0 75%, transparent 0)`,
        size: '4.5rem 4.5rem',
        position: 'center',
      };
    case 'waves':
      return {
        image: `repeating-radial-gradient(ellipse at 18% 52%, transparent 0 4.2rem, ${translucent(secondary, 18)} 4.28rem 4.42rem, transparent 4.5rem 8.5rem)`,
        size: 'auto',
        position: 'center',
      };
    case 'hex':
      return {
        image: `repeating-linear-gradient(60deg, transparent 0 3.1rem, ${translucent(accent, 16)} 3.12rem 3.22rem, transparent 3.24rem 6.2rem)`,
        size: 'auto',
        position: 'center',
      };
    case 'hazard':
      return {
        image: `repeating-linear-gradient(120deg, transparent 0 4rem, ${translucent(accent, 19)} 4rem 4.45rem, transparent 4.45rem 8rem, ${translucent(secondary, 13)} 8rem 8.16rem, transparent 8.16rem 12rem)`,
        size: 'auto',
        position: 'center',
      };
    case 'minimal':
      return {
        image: `linear-gradient(120deg, transparent 0 47%, ${translucent(accent, 22)} 49% 50%, transparent 52%)`,
        size: 'auto',
        position: 'center',
      };
  }
}

export function themeStagePresentation(table: TableDefinition,includeArtwork=true): ThemeStagePresentation {
  const preset = getThemePreset(table.theme.presetId);
  const accent = table.theme.accentColor || preset.palette.accent;
  const secondary = table.theme.secondaryColor || preset.palette.secondary;
  const base = table.theme.backgroundColor || preset.palette.background;
  const requestedPattern = table.theme.playfieldPattern;
  const pattern = requestedPattern && KNOWN_PATTERNS.has(requestedPattern)
    ? requestedPattern as PlayfieldPattern
    : preset.pattern;
  const motif = motifFor(pattern, accent, secondary);
  const recognizedPreset = Boolean(table.theme.presetId && preset.id === table.theme.presetId);
  const art = machineArtForTable(table);

  const proceduralImages = [
    `radial-gradient(circle at 20% 22%, ${translucent(accent, 34)}, transparent 34%)`,
    `radial-gradient(circle at 78% 30%, ${translucent(secondary, 28)}, transparent 38%)`,
    `linear-gradient(145deg, ${translucent(accent, 7)}, transparent 40%, ${translucent(secondary, 8)})`,
    motif.image,
  ];
  const proceduralSizes = ['auto', 'auto', 'auto', motif.size];
  const proceduralPositions = ['center', 'center', 'center', motif.position];

  if (art && includeArtwork) {
    const framePosition = (art.backglassFrame / (THEME_ART_FRAME_COUNT - 1)) * 100;
    proceduralImages.unshift(`url(${THEME_ART_ATLAS_URL})`);
    proceduralSizes.unshift(`100% ${THEME_ART_FRAME_COUNT * 100}%`);
    proceduralPositions.unshift(`center ${framePosition}%`);
  }

  return {
    backgroundColor: base,
    backgroundImage: proceduralImages.join(', '),
    backgroundSize: proceduralSizes.join(', '),
    backgroundPosition: proceduralPositions.join(', '),
    themeName: recognizedPreset ? preset.name : 'Custom',
  };
}
